// Catalog.ID Client — `mail_address` claim create
// ============================================================================
// The side-effecting half of the `create-mail-address` key-grant mode, kept out
// of `mail-address-handlers.ts` so the gate logic there stays unit-testable with
// no network and no modals — the `ceremony-keystore.ts` / `chat-address-store.ts`
// split, for the same reason.
//
// `mail_address` is created PROGRAMMATICALLY, never by the generic claim form:
// it is deliberately absent from the creatable-type list in
// `pane-types/claims-list-pane.ts` (a display label only), exactly like
// `chat_address` and `registry_account_stream`. Migration §5.3 states the rule;
// this module is the only writer.
//
// NO SHARE STEP, unlike `chat-address-store.ts`. Chat's mutual share IS its
// consent record; mail deliberately does not model consent to be mailed
// (§5.3), so there is nobody to share with. What makes a mail address usable by
// others is PUBLICATION plus a directory registration — both separate,
// deliberate acts on their own surfaces, neither of them a side effect of this
// module. That is why the create-mail-address canonical digest commits to the
// value alone: the value determines exactly what this module does.
//
// IDEMPOTENCY IS STILL THE POINT. A device may run the flow again after a
// restart, and a second device of the same account may run it independently:
//
//   * an existing `mail_address` claim holding EXACTLY this value is reused —
//     never duplicated.
//
// A value MISMATCH (the mail_encrypt key rotated, the operator changed) is a
// NEW claim, not an update: `PUT /api/claims/{uuid}` rewrites only
// `{encryptedValue, nonce}` and keeps the original wrapped symmetric key, so
// every existing reader of the published record would silently start reading
// the new one. Stale claims are left in place — revoking them is the user's
// call, never a side effect of a module request.

import * as accountApi from '../api/account';
import * as claimsApi from '../api/claims';
import * as aes from '../crypto/aes';
import { encryptClaim, unwrapSymmetricKeyChain } from '../crypto/claims';
import { keys } from '../state/keys';
import { ATTR_ENCRYPT_KEY_ID, ClaimShareError } from '../state/claim-sharing';

/** The CID claim type carrying a mail address record (migration §5.3). */
export const MAIL_ADDRESS_CLAIM_TYPE = 'mail_address';

export interface CreateMailAddressInput {
    /** JCS canonical JSON of the `mail_address.v1` record — opaque here. */
    mailAddressValue: string;
    /** Human label for the claims list. Encrypted under the claim's own key. */
    label?: string;
}

export interface CreateMailAddressResult {
    /** Server-assigned. NEVER predicted (ownership D1). */
    claimUuid: string;
    /** False when an existing claim with this exact value was reused. */
    created: boolean;
}

/**
 * Find-or-create this account's `mail_address` claim for `mailAddressValue`.
 * Idempotent. Does NOT publish and does NOT share.
 */
export async function createMailAddressClaim(
    input: CreateMailAddressInput,
): Promise<CreateMailAddressResult> {
    const existing = await findMailAddressClaim(input.mailAddressValue);
    if (existing) return { claimUuid: existing, created: false };

    const claimUuid = await postMailAddressClaim(input.mailAddressValue, input.label);
    return { claimUuid, created: true };
}

/**
 * The uuid of an existing `mail_address` claim whose decrypted value is
 * byte-identical to `mailAddressValue`, or null.
 *
 * Values are compared after decryption, not by ciphertext: every claim gets a
 * fresh symmetric key and nonce, so two claims holding the same address have
 * completely different ciphertext.
 */
export async function findMailAddressClaim(mailAddressValue: string): Promise<string | null> {
    const summaries = await claimsApi.listClaims();
    const candidates = summaries.filter(
        c => c.claimType === MAIL_ADDRESS_CLAIM_TYPE && !c.revokedAt);

    for (const summary of candidates) {
        try {
            const detail = await claimsApi.getClaim(summary.claimUuid);
            const value = await decryptOwnClaimValue(detail);
            if (value === mailAddressValue) return summary.claimUuid;
        } catch {
            // One unreadable claim (a wrap this device cannot peel) must not
            // hide the others, and must not turn a reuse into a failure — fall
            // through and let the caller create a fresh one.
            continue;
        }
    }
    return null;
}

/** Decrypt a claim this account owns, walking the attr_encrypt rotation chain. */
async function decryptOwnClaimValue(
    detail: { encryptedValue: string; nonce: string; keys: { recipientType: string; encryptedKey: string; keyIndex: number }[] },
): Promise<string> {
    const selfKey = detail.keys.find(k => k.recipientType === 'self');
    if (!selfKey) throw new ClaimShareError('claim-has-no-self-key', 'No self key found');

    const symKey = await unwrapSymmetricKeyChain(
        selfKey.encryptedKey, keys.getKeyChain(ATTR_ENCRYPT_KEY_ID), selfKey.keyIndex);
    const plaintext = await aes.decrypt(
        symKey, aes.fromBase64(detail.nonce), aes.fromBase64(detail.encryptedValue));
    return aes.decodeUtf8(plaintext);
}

/** Encrypt and POST a fresh `mail_address` claim. Returns the server's uuid. */
async function postMailAddressClaim(
    mailAddressValue: string, label?: string,
): Promise<string> {
    const pubKeys = await accountApi.getOwnPublicKeys();
    const attrPub = pubKeys.find(k => k.keyId === ATTR_ENCRYPT_KEY_ID);
    if (!attrPub) {
        throw new ClaimShareError('no-attr-encrypt-key', 'This account has no attr_encrypt key');
    }

    const encrypted = await encryptClaim(
        mailAddressValue, aes.fromBase64(attrPub.publicKey), label);

    const created = await claimsApi.createClaim({
        claimType: MAIL_ADDRESS_CLAIM_TYPE,
        encryptedValue: encrypted.encryptedValue,
        nonce: encrypted.nonce,
        encryptedKey: encrypted.encryptedKey,
        ...(encrypted.encryptedLabel ? { encryptedLabel: encrypted.encryptedLabel } : {}),
        ...(encrypted.labelNonce ? { labelNonce: encrypted.labelNonce } : {}),
    });
    return created.claimUuid;
}
